import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useOrganisations,
  useOrganisationModules,
  useSetOrganisationModules,
  type OrganisationModuleState,
} from '../lib/queries.js';
import { useAuth } from '../lib/auth.js';
import { relativeTime } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  PageHeader,
  PermissionDeniedState,
  SearchableSelect,
  Skeleton,
  Textarea,
} from '../components/ui/index.js';

/**
 * Module control: tick a box, hit Save, the hospital has the module.
 *
 * WHAT THIS SCREEN KNOWS, AND WHAT IT DOES NOT. Control has no heartbeat field
 * carrying module state, so it cannot independently confirm what a hospital
 * currently has switched on. It knows only what it has successfully commanded.
 * A module Control has never commanded is therefore shown as "Not set here" —
 * NOT as an empty tick box, which would read as "off" and is a different and
 * false claim. The hospital may well have that module on via its pricing band;
 * Control genuinely cannot see it, and says so.
 *
 * WHY SAVE DOES NOT PROMISE. Saving queues signed provisioning commands. The
 * tenant applies them on its own schedule and acknowledges. Until then the row
 * reads "Queued", because a tick that has not reached the hospital is not a
 * setting — it is a request.
 */

function stateLabel(m: OrganisationModuleState): {
  text: string;
  tone: 'success' | 'neutral' | 'warning';
} {
  if (m.pendingAction) {
    return {
      text: m.pendingAction === 'enable_module' ? 'Turning on…' : 'Turning off…',
      tone: 'warning',
    };
  }
  if (m.enabled === null) return { text: 'Not set here', tone: 'neutral' };
  return m.enabled ? { text: 'On', tone: 'success' } : { text: 'Off', tone: 'neutral' };
}

export function ModulesPage() {
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org') ?? undefined;

  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<string | null>(null);

  // pageSize maxes out at 100 server-side (PageQuerySchema): asking for 200
  // fails validation and the picker silently renders "No matching results",
  // which reads as "you have no hospitals" rather than "the request was
  // rejected". Sorted by name so the list is navigable rather than in
  // last-updated order.
  const orgsQuery = useOrganisations(
    new URLSearchParams({ pageSize: '100', sortBy: 'displayName', sortDir: 'asc' }),
  );
  const modulesQuery = useOrganisationModules(orgId);
  const save = useSetOrganisationModules(orgId);

  const canRead = hasPermission('entitlement.read');
  const canWrite = hasPermission('provisioning.dispatch');

  const modules = modulesQuery.data?.modules ?? [];

  const grouped = useMemo(() => {
    const byGroup = new Map<string, OrganisationModuleState[]>();
    for (const m of modules) {
      const list = byGroup.get(m.group) ?? [];
      list.push(m);
      byGroup.set(m.group, list);
    }
    return [...byGroup.entries()];
  }, [modules]);

  /** Effective tick state: the operator's draft if touched, else what Control knows. */
  const ticked = (m: OrganisationModuleState): boolean =>
    m.key in draft ? draft[m.key]! : m.enabled === true;

  const changedKeys = useMemo(
    () =>
      Object.keys(draft).filter((k) => {
        const m = modules.find((x) => x.key === k);
        if (!m) return false;
        return draft[k] !== (m.enabled === true) || m.enabled === null;
      }),
    [draft, modules],
  );

  if (!canRead) {
    return <PermissionDeniedState what="module access" />;
  }

  const onSave = async () => {
    setResult(null);
    const desired: Record<string, boolean> = {};
    for (const k of changedKeys) desired[k] = draft[k]!;
    const res = await save.mutateAsync({ desired, reason });
    setDraft({});
    setReason('');
    setResult(
      res.queued.length === 0
        ? 'Nothing to do — every module was already in the state you asked for.'
        : `Queued ${res.queued.length} change${res.queued.length === 1 ? '' : 's'}. ` +
            'The hospital will have them once its platform picks the commands up.',
    );
  };

  return (
    <div className="space-y-6" data-testid="modules-page">
      <Breadcrumbs items={[{ label: 'Modules' }]} />
      <PageHeader
        title="Module access"
        subtitle="Turn a hospital's modules on or off. Changes are sent to their platform as signed commands."
      />

      <Card>
        <CardBody>
          <SearchableSelect
            label="Hospital"
            placeholder="Choose a hospital…"
            value={orgId ?? ''}
            onChange={(val) => {
              setDraft({});
              setResult(null);
              setParams(val ? { org: val } : {});
            }}
            options={(orgsQuery.data?.items ?? []).map((o) => ({
              value: o.id,
              label: o.displayName,
            }))}
          />
        </CardBody>
      </Card>

      {!orgId && (
        <EmptyState
          title="Choose a hospital"
          description="Pick a hospital above to see and change which modules it has."
        />
      )}

      {orgId && modulesQuery.isLoading && <Skeleton rows={8} />}

      {orgId && modulesQuery.error && (
        <ErrorState
          description={(modulesQuery.error as Error).message}
          onRetry={() => void modulesQuery.refetch()}
        />
      )}

      {orgId && modulesQuery.data && (
        <>
          {!canWrite && (
            <Card>
              <CardBody>
                <p className="text-sm text-text-muted" data-testid="readonly-notice">
                  You have read-only access to module settings. Changing a hospital's modules needs
                  the provisioning permission — this screen shows the current state only.
                </p>
              </CardBody>
            </Card>
          )}
          {modulesQuery.data.installationId === null && (
            <Card>
              <CardBody>
                <p className="text-sm text-warning" data-testid="no-installation-warning">
                  This hospital has no installation reporting to Control, so module changes cannot
                  be delivered to it yet.
                </p>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody>
              <p className="text-xs text-text-muted">
                Control shows what it has sent to this hospital and had confirmed. A module marked{' '}
                <strong>Not set here</strong> has never been changed from Control — the hospital may
                already have it through its plan, which Control cannot see.
              </p>
            </CardBody>
          </Card>

          {grouped.map(([group, items]) => (
            <Card key={group}>
              <CardBody>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
                  {group}
                </h3>
                <ul className="divide-y divide-border">
                  {items.map((m) => {
                    const s = stateLabel(m);
                    return (
                      <li
                        key={m.key}
                        className="flex items-center justify-between gap-4 py-2"
                        data-testid={`module-row-${m.key}`}
                      >
                        <label className="flex flex-1 items-center gap-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border"
                            checked={ticked(m)}
                            disabled={!canWrite || Boolean(m.pendingAction)}
                            data-testid={`module-toggle-${m.key}`}
                            onChange={(e) => setDraft((d) => ({ ...d, [m.key]: e.target.checked }))}
                          />
                          <span className="text-sm text-text">{m.label}</span>
                        </label>
                        <div className="flex items-center gap-2">
                          {m.confirmedAt && (
                            <span className="text-xs text-text-muted">
                              {relativeTime(m.confirmedAt)}
                            </span>
                          )}
                          <Badge tone={s.tone}>{s.text}</Badge>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          ))}

          {canWrite && (
            <Card>
              <CardBody className="space-y-3">
                <Textarea
                  label="Why are you changing this?"
                  hint="Recorded in the audit trail — for example the email asking for the module."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="modules-reason"
                />
                <div className="flex items-center gap-3">
                  <Button
                    variant="primary"
                    data-testid="modules-save"
                    disabled={
                      changedKeys.length === 0 ||
                      reason.trim().length < 8 ||
                      save.isPending ||
                      modulesQuery.data.installationId === null
                    }
                    onClick={() => void onSave()}
                  >
                    {save.isPending ? 'Saving…' : `Save ${changedKeys.length || ''}`.trim()}
                  </Button>
                  {changedKeys.length > 0 && (
                    <span className="text-xs text-text-muted" data-testid="modules-change-count">
                      {changedKeys.length} module{changedKeys.length === 1 ? '' : 's'} to change
                    </span>
                  )}
                </div>
                {save.error && <ErrorState description={(save.error as Error).message} />}
                {result && (
                  <p className="text-sm text-success" data-testid="modules-result">
                    {result}
                  </p>
                )}
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
